import { GQLProductFragment, GQLProductSideAvailableDecorationType, GQLProductSideSideType, GQLStoreFragment } from '@theprintbar/graphql';
import {
	Decoration,
	DecorationServer,
	DesignParamAction,
	Merch,
	MerchFormServer,
	WebRoutes,
	cloneDeep,
	decorationIsSamedayCompatible,
	isIOS,
	getClosestPantone,
	getPantoneColours,
	getSortedSizes,
	hexToPixel,
	invariant,
	withDomain,
} from '@theprintbar/shared';
import axios from 'axios';
import type { CreativeEditorInstance } from '../../CreativeEditor';
import { editorTools } from '../../CreativeEditor/editorTools';

export interface StatusUpdateProps {
	type: 'generating' | 'uploading' | 'submitting';
	title: string;
	progress?: {
		currentBytes: number;
		totalBytes: number;
	};
}

interface FinalMerchProps {
	editor: CreativeEditorInstance;
	product: GQLProductFragment;
	currentMerch: Merch;
	webDomain: string;
	action?: DesignParamAction;
	store?: GQLStoreFragment | null;
	statusUpdateCallback: (data?: StatusUpdateProps) => void;
}

type LabelData = {
	colour: 'white' | 'black';
	size: string;
};

type DecIndex = { decoration: Decoration; teamNameIndex?: number; labelData?: LabelData };
type ServerDecIndex = { decoration: DecorationServer; teamNameIndex?: number; labelData?: LabelData };

export async function getFinalMerch({
	editor,
	product,
	currentMerch,
	webDomain,
	action,
	store,
	statusUpdateCallback,
}: FinalMerchProps): Promise<MerchFormServer> {
	try {
		invariant(editor, 'No editor provided');
		const newMerch = cloneDeep(currentMerch) as Merch;

		newMerch.decorations = newMerch.decorations.filter(d => {
			const decorationExists = !!d.artwork?.size && !!d.meta.width && !!d.meta.height;
			const samedayCompatible = decorationIsSamedayCompatible(d);
			return decorationExists && (!!store || !newMerch.sameday || samedayCompatible);
		});

		if (!newMerch.teamNames?.available.length) {
			newMerch.teamNames = undefined;
		}

		//Remove any no longer relevant team names
		if (newMerch.teamNames) {
			newMerch.teamNames.selected = newMerch.teamNames.selected
				.map(teamName => ({
					...teamName,
					inputs: teamName.inputs.filter(i => newMerch.teamNames?.available.includes(i.id)),
				}))
				.filter(teamName => teamName.inputs.length > 0);
		}

		// Array to store the generated images
		const generatedDecorations: DecIndex[] = [];
		// Array to store the uploaded images
		const uploadedDecorations: ServerDecIndex[] = [];

		function getTeamNameData(teamNameIndex?: number) {
			let defaultTeamNameData = { teamNameString: '', teamNames: [] };
			if (typeof teamNameIndex !== 'number') return defaultTeamNameData;
			const teamNameData = newMerch.teamNames?.selected[teamNameIndex];
			if (!teamNameData) return defaultTeamNameData;
			return {
				teamNameString: ` ${teamNameData.inputs.map(i => i.name).join(' ')}`,
				teamNames: teamNameData.inputs,
			};
		}

		// Helper function to simulate image generation
		async function generateDecoration({ decoration, teamNameIndex, labelData }: DecIndex) {
			invariant(editor, 'No editor provided');
			try {
				const { teamNameString, teamNames } = getTeamNameData(teamNameIndex);
				statusUpdateCallback({
					type: 'generating',
					title: `Generating ${decoration.side} decoration${teamNameString}`,
				});

				if (!!labelData) {
					editorTools.updateNeckLabelSize(editor, labelData.size);
				}

				await new Promise(resolve => setTimeout(resolve, 0));

				// Large-format sides declare the density they actually need, which is
				// well under 300 — a 1m x 1.5m page at 300 DPI is ~209MP and trips the
				// device texture cap, so the export silently downsamples to whatever
				// the hardware managed. Starting at the side's own target keeps that
				// render within reach and lands the DPI we wanted first time.
				// Only applies to composed pages: a solo raster still reports its own
				// source-derived DPI (see getArtwork).
				const productSide = product.sides.find(s => s.side === decoration.side);

				const artworkAndMeta = await editorTools.getArtworkFromPage({
					editor,
					domain: webDomain,
					colourHex: product.customDecorationFixedColour,
					decoration,
					dpi: productSide?.minimumAcceptableDPI || 300,
					teamNames,
					isFinal: true,
				});

				// No transparency for DTF, either fully transparent or fully opaque
				const newDecoration = {
					...decoration,
					...artworkAndMeta,
					artwork:
						decoration.type === GQLProductSideAvailableDecorationType.Dtf
							? await modifyAlpha(artworkAndMeta?.artwork as Blob)
							: artworkAndMeta?.artwork,
					printReady: artworkAndMeta?.printReady,
					meta: {
						...artworkAndMeta.meta,
						requestedColours: decoration.meta.requestedColours,
						stitches: decoration.meta.stitches,
						useSuppliedEmbFile: decoration.meta.useSuppliedEmbFile,
					},
				};

				generatedDecorations.push({
					decoration: newDecoration,
					teamNameIndex,
					labelData,
				});
			} catch (e) {
				console.error('Error generating decoration:', e);
				throw e;
			}
		}

		async function modifyAlpha(image: Blob): Promise<Blob> {
			const img = new Image();
			const imageUrl = URL.createObjectURL(image);
			img.src = imageUrl;

			await new Promise(resolve => (img.onload = resolve));

			const canvas = document.createElement('canvas');
			canvas.width = img.width;
			canvas.height = img.height;
			const ctx = canvas.getContext('2d');
			if (!ctx) throw new Error('Failed to get canvas context');
			ctx.drawImage(img, 0, 0);

			const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
			const data = imageData.data;

			for (let i = 0; i < data.length; i += 4) {
				// Iterate by 4 for RGBA
				const alpha = data[i + 3] / 255; // data[i + 3] is the alpha channel / transparency
				data[i + 3] = alpha < 0.5 ? 0 : 255; // Set to transparent if alpha is less than 0.5, otherwise fully opaque
			}

			ctx.putImageData(imageData, 0, 0);

			URL.revokeObjectURL(imageUrl); // Clean up the object URL

			return new Promise<Blob>(resolve => canvas.toBlob(blob => resolve(blob!), 'image/png'));
		}

		type UploadUrlResponse = { getUrl: string; putUrl: string; key: string };
		async function getUploadURL(name: string) {
			const res = await fetch(withDomain(WebRoutes.API.images.getSignedUpload, webDomain), {
				method: 'POST',
				body: name,
			});
			invariant(res.ok, 'Failed to upload design, please try again');
			return (await res.json()) as UploadUrlResponse;
		}

		// Helper function to simulate image upload
		async function uploadDecoration({ decoration, teamNameIndex, labelData }: DecIndex) {
			const artworkName = `${product.name}${decoration.side}${Date.now()}.png`;
			const graphic = decoration.meta.graphicData?.[0];
			const singleGraphic = !decoration.artwork && graphic && decoration.meta.sceneItems === 1 && !graphic.manipulated;

			if (decoration.meta.graphicData?.length === 1 && !!graphic && !singleGraphic) {
				//Clear the designIds and such as they are no longer valid
				decoration.meta.graphicData = [
					{
						...graphic,
						designId: undefined,
					},
				];
			}

			const printReadyName = `${product.name}${decoration.side}${Date.now()}.pdf`;
			const files: Record<string, { name: string; blob: Blob; uploadUrl: UploadUrlResponse } | undefined> = {
				artwork: decoration.artwork
					? {
							name: artworkName,
							blob: decoration.artwork,
							//We dont need the upload url for single graphics as they are already in the editor
							uploadUrl: await getUploadURL(artworkName),
					  }
					: undefined,
				attachment: decoration.attachment
					? {
							name: decoration.attachment.file.name,
							blob: decoration.attachment.file,
							uploadUrl: await getUploadURL(decoration.attachment.file.name),
					  }
					: undefined,
				trueview: decoration.meta.trueview
					? {
							name: `trueview-${artworkName}`,
							blob: decoration.meta.trueview,
							uploadUrl: await getUploadURL(`trueview-${artworkName}`),
					  }
					: undefined,
				printReady: decoration.printReady
					? {
							name: printReadyName,
							blob: decoration.printReady,
							uploadUrl: await getUploadURL(printReadyName),
					  }
					: undefined,
			};

			const totalSize = Object.values(files).reduce((acc, file) => acc + (file?.blob?.size || 0), 0);
			function uploadPing(bytes: number) {
				statusUpdateCallback({
					type: 'uploading',
					title: `Uploading ${decoration.side} decoration${teamNameString}`,
					progress: {
						currentBytes: progress,
						totalBytes: totalSize,
					},
				});
			}

			let progress = 0;
			const { teamNameString } = getTeamNameData(teamNameIndex);

			for (const file in files) {
				const { uploadUrl, blob } = files[file] || {};
				if (!uploadUrl?.putUrl || !blob) continue;

				uploadPing(progress);
				const res = await axios.put(uploadUrl.putUrl, blob, {
					onUploadProgress: e => {
						if (!e.total) return;
						progress += e.loaded;
						uploadPing(progress);
					},
					headers: {
						'Content-Type': blob.type,
						'Cross-Origin-Embedder-Policy': 'unsafe-none',
						'Cross-Origin-Opener-Policy': 'same-origin-allow-popups',
					},
				});

				invariant(res.status === 200, 'Failed to upload image');
			}

			uploadPing(totalSize);

			uploadedDecorations.push({
				decoration: {
					...decoration,
					meta: {
						...decoration.meta,
						trueview: files.trueview ? { size: files.trueview.blob.size, file: files.trueview.uploadUrl.key } : undefined,
					},
					artwork: singleGraphic
						? {
								size: decoration.meta.width * decoration.meta.height * 4, // Assuming 4 bytes per pixel for RGBA
								file: graphic.designId!,
						  }
						: files.artwork
						? { size: files.artwork.blob.size, file: files.artwork.uploadUrl.key }
						: undefined,
					attachment: files.attachment ? { size: files.attachment.blob.size, file: files.attachment.uploadUrl.key } : undefined,
					printReady: files.printReady ? { size: files.printReady.blob.size, file: files.printReady.uploadUrl.key } : undefined,
				},
				teamNameIndex,
				labelData,
			});
		}

		async function handleImageProcessing(decorations: DecIndex[]) {
			try {
				if (!editor) return;
				const totalDecorations = decorations.length;
				const labelDecorations = decorations.filter(d => !!d.labelData);
				const nonLabelDecorations = decorations.filter(d => !d.labelData);

				if (!!labelDecorations.length) {
					const pantones = await getPantoneColours();
					//Do the whiteLabels first then the blackLabels
					const whiteLabels = decorations.filter(d => d.labelData?.colour === 'white');
					const whitePantone = getClosestPantone(hexToPixel('#FFFFFF'), pantones);
					await editorTools.recolourSide(GQLProductSideSideType.Label, {
						domain: webDomain,
						editor,
						colourList: [whitePantone],
						replacements: {
							[`${whitePantone.name}`]: whitePantone,
						},
						neckOnly: true,
					});
					for (const whiteLabel of whiteLabels) {
						await generateDecoration(whiteLabel);
					}

					const blackLabels = decorations.filter(d => d.labelData?.colour === 'black');
					const blackPantone = getClosestPantone(hexToPixel('#000000'), pantones);
					await editorTools.recolourSide(GQLProductSideSideType.Label, {
						domain: webDomain,
						editor,
						colourList: [blackPantone],
						replacements: {
							[`${blackPantone.name}`]: blackPantone,
						},
						neckOnly: true,
					});
					for (const blackLabel of blackLabels) {
						await generateDecoration(blackLabel);
					}
				}

				// Team names must be sequential regardless: each one sets scene variables that
				// the next would clobber.
				//
				// Otherwise the split is iOS vs everything else. Each export peaks at roughly
				// 8 bytes per pixel — the canvas backing store plus the getImageData copy in
				// cropTransparentEdges — and a 300 DPI print area is tens of megapixels, so
				// running sides concurrently multiplies that peak by the number of decorated
				// sides. Every browser on iOS is WebKit, which kills the tab outright when it
				// crosses a device-dependent memory ceiling that JS cannot query, so there we
				// hold the peak at one side's worth. Everywhere else the headroom exists and
				// the parallelism is worth keeping.
				if (isIOS() || nonLabelDecorations.some(d => d.teamNameIndex !== undefined)) {
					for (const decoration of nonLabelDecorations) {
						await generateDecoration(decoration);
					}
				} else {
					await Promise.all(nonLabelDecorations.map(d => generateDecoration(d)));
				}

				// Generate and upload in parallel
				while (uploadedDecorations.length < totalDecorations) {
					// Start uploading an image if there's one ready
					if (!!generatedDecorations.length) {
						const decoration = generatedDecorations.pop();
						if (!decoration) continue;

						await uploadDecoration(decoration);
					}

					// Wait a short time before checking again
					await new Promise(resolve => setTimeout(resolve, 100));
				}
			} catch (e) {
				throw e;
			}
		}

		const decsToProcess: DecIndex[] = [];

		product.sides.map(async side => {
			const decorationIndex = newMerch.decorations.findIndex(decoration => decoration.side === side.side);
			if (decorationIndex === -1) return;

			const decoration = newMerch.decorations[decorationIndex];
			if (!decoration?.artwork?.size) return;

			if (side.side === GQLProductSideSideType.Label) {
				getSortedSizes(product).forEach(({ variant }) => {
					decsToProcess.push({ decoration, labelData: { colour: 'white', size: variant.size.code } });
					decsToProcess.push({ decoration, labelData: { colour: 'black', size: variant.size.code } });
				});
			} else if (!!newMerch.teamNames) {
				newMerch.teamNames.selected.forEach((_, teamNameIndex) => {
					decsToProcess.push({ decoration, teamNameIndex });
				});
			} else {
				decsToProcess.push({ decoration });
			}
		});

		await handleImageProcessing(decsToProcess);

		statusUpdateCallback({
			type: 'generating',
			title: `Saving scene...`,
		});

		const scene = {
			name: 'scene.txt',
			blob: new Blob([await editorTools.getSceneString(editor)], {
				type: 'text/plain',
			}),
			uploadUrl: await getUploadURL('scene.txt'),
		};

		await axios.put(scene.uploadUrl.putUrl, scene.blob, {
			headers: {
				'Content-Type': scene.blob.type,
				'Cross-Origin-Embedder-Policy': 'unsafe-none',
				'Cross-Origin-Opener-Policy': 'same-origin-allow-popups',
			},
		});

		let serverMerch: MerchFormServer = {
			...newMerch,
			action,
			scene: {
				file: scene.uploadUrl.key,
				size: scene.blob.size,
			},
			labels: [],
			decorations: [],
			teamNames: newMerch.teamNames
				? {
						...newMerch.teamNames,
						selected: newMerch.teamNames.selected.map(t => ({ ...t, decorations: [] })),
				  }
				: undefined,
		};

		let hasSetLabel = false;
		uploadedDecorations.forEach(({ decoration, teamNameIndex, labelData }) => {
			if (typeof teamNameIndex === 'number' && !!serverMerch.teamNames) {
				serverMerch.decorations.push(decoration);
				serverMerch.teamNames.selected[teamNameIndex].decorations.push(decoration);
			} else if (!!labelData) {
				serverMerch.labels?.push({
					...decoration,
					size: labelData.size,
					colour: labelData.colour,
				});
				if (!hasSetLabel) {
					serverMerch.decorations.push(decoration);
					hasSetLabel = true;
				}
			} else {
				serverMerch.decorations.push(decoration);
			}
		});

		statusUpdateCallback();

		return serverMerch;
	} catch (e) {
		console.log('Unable to generate final merch');
		throw e;
	}
}
